import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import config from '@/config';
import useCache from '@/hooks/useCache';
import useNavigateToArtwork from '@/hooks/useNavigateToArtwork';
import { ARTWORK_CONTINUATION_POLICIES } from '@/shared/constants/homeCarousel';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import Loader from '@/shared-components/loader/Loader';
import Text from '@/shared-components/text/components/Text';
import { isRestrictedArtwork } from '@/utils/artworkVisibility';

import ArtistArtworksHeader from '../../components/ArtistArtworksHeader';

import VirtualList from './components/VirtualList';

import './index.css';

/**
 * Normaliza un artista para el header:
 * - Garantiza user_type: 'artist' | 'gallery_artist'
 * - Alinea los campos que usa el header (user_name, user_username, user_image, artist_nationality/birth/death)
 */
function normalizeArtistForHeader(raw) {
  if (!raw || typeof raw !== 'object') return raw;

  // Si ya viene marcado como 'artist', usamos tal cual
  if (raw.user_type === 'artist') {
    return {
      ...raw,
      user_type: 'artist',
      // Asegura estructura básica por si faltan campos
      user_image: raw.user_image ?? null,
      user_name: raw.user_name ?? raw.artist_name ?? '',
      artist_nationality: raw.artist_nationality ?? raw.gallery_artist?.artist_nationality,
      artist_birth: raw.artist_birth ?? raw.gallery_artist?.artist_birth,
      artist_death: raw.artist_death ?? raw.gallery_artist?.artist_death,
    };
  }

  // Si ya viene marcado como 'gallery_artist' o tiene campos típicos de GA
  if (raw.user_type === 'gallery_artist' || raw.artist_name || raw.artist_image) {
    return {
      ...raw,
      user_type: 'gallery_artist',
      // El header espera user_image?.image_id, por eso alineamos a ese nombre
      user_image: raw.user_image ?? raw.artist_image ?? null,
      // El header muestra "name" en un <Text> que saca user_name para artist o artist_name para gallery_artist
      // Dejamos ambos por compatibilidad:
      user_name: raw.user_name ?? raw.artist_name ?? '',
      artist_name: raw.artist_name ?? raw.user_name ?? '',
      // Un gallery_artist no tiene perfil público -> user_username puede ser vacío/undefined
      user_username: raw.user_username ?? '',
      artist_nationality: raw.artist_nationality,
      artist_birth: raw.artist_birth,
      artist_death: raw.artist_death,
    };
  }

  // Fallback: si no podemos inferir, asumimos 'artist'
  return {
    ...raw,
    user_type: 'artist',
    user_image: raw.user_image ?? null,
    user_name: raw.user_name ?? raw.artist_name ?? '',
  };
}

export default function Artworks({ artist, profileData, internal, close, showClose = true, queryEndpoint = null, cacheKey = null }) {
  // Normaliza el artista para el header y para la lógica de navegación
  const headerArtist = useMemo(() => normalizeArtistForHeader(artist), [artist]);

  const endpoint = queryEndpoint
    ? queryEndpoint
    : `/gallery/${profileData._id}/artist/${artist._id}/artworks?artist_type=${
      // ¡OJO! La API espera 'artist' o 'gallery_artist' en artist_type (contrario al flag booleano antiguo).
      headerArtist?.user_type === 'artist' ? 'artist' : 'gallery_artist'
    }`;

  const cacheKeyToUse = cacheKey ? cacheKey : `artworks_of_${artist._id}_in_${profileData._id}`;
  const profileTitle =
    profileData?.user_profile_name
    ?? profileData?.user_name
    ?? profileData?.artist_name
    ?? profileData?.name
    ?? profileData?.user_username
    ?? headerArtist?.user_name
    ?? headerArtist?.artist_name
    ?? '';

  const invalidateWhen = profileData
    ? internal
      ? ['EDIT_ARTWORK', 'DELETE_ARTWORK', 'NEW_ARTIST', `${profileData._id}_REFRESH`]
      : [`${profileData._id}_REFRESH`]
    : [];

  const { loading, fetchData, loadMoreData } = useCache(cacheKeyToUse, `${config.apis.api.url}${endpoint}`, {
    injectToken: true,
    includeSearchQueries: {
      // ya va en la URL
    },
    invalidateWhen,
  });

  const navigateToArtwork = useNavigateToArtwork(
    cacheKeyToUse,
    fetchData,
    endpoint,
    {
      type: 'profile-navigation',
      label: profileTitle,
      continuation: endpoint.includes("/galleryartist/")
        ? ARTWORK_CONTINUATION_POLICIES.SUARTEWORLD
        : undefined,
    },
  );

  const navigate = useNavigate();

  function navigateToProfileIfPossible() {
    if (headerArtist?.user_type === 'artist' && headerArtist?.user_username) {
      navigate(`/user/${headerArtist.user_username}`, { state: { from: true } });
    }
  }

  const safeData = Array.isArray(fetchData?.data)
    ? fetchData.data.filter((item) => !isRestrictedArtwork(item))
    : [];
  const safeFetchData = fetchData ? { ...fetchData, data: safeData } : { data: safeData, totalDocs: 0 };
  const artworkCount =
    (typeof fetchData?.totalDocs === 'number' && Number.isFinite(fetchData.totalDocs) ? fetchData.totalDocs : null)
    ?? (typeof artist?.artwork_count === 'number' && Number.isFinite(artist.artwork_count) ? artist.artwork_count : null)
    ?? safeData.length;
  const artistNameFallback =
    headerArtist?.user_name
    ?? headerArtist?.artist_name
    ?? headerArtist?.user_username
    ?? null;

  return (
    <>
      <div className="artist-artworks-content__header-row">
        <div
          className={`artist-artworks-content__header ${
            headerArtist?.user_type === 'artist' ? 'click' : ''
          }`}
          onClick={navigateToProfileIfPossible}
        >
          {/* Aquí pasamos el artista normalizado, como "antes" esperaba el header */}
          <ArtistArtworksHeader artistData={headerArtist} preview />
        </div>
        {showClose && <XIcon className="artist-artworks-content__close" onClick={close} />}
      </div>

      <Text className="artist-artworks-content__artwork-count" paragraph>
        {artworkCount} artworks
      </Text>

      {loading ? (
        <Loader className="artist-artworks-content__content-spinner" thin />
      ) : (
        <VirtualList
          items={safeFetchData}
          onLoadMore={loadMoreData}
          navigateToArtwork={navigateToArtwork}
          close={close}
          artistNameFallback={artistNameFallback}
        />
      )}
    </>
  );
}
